import * as Styled from './AddScannerModal.styles';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AddScannerFormData } from './types';
import { Form } from 'features/common/components/Form';
import { addMember } from '../../services';
import { useForm } from 'react-hook-form';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { validationSchema } from './utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { airbrake } from 'ErrorBoundary';

interface AddScannerModalProps {
	isOpen: boolean;
	onClose: () => void;
	eventId: string;
}

export const AddScannerModal = ({ isOpen, onClose }: AddScannerModalProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showNotification, TYPES } = useToastNotification();

	const formMethods = useForm<AddScannerFormData>({
		defaultValues: {
			email: '',
			full_name: '',
		},
		resolver: yupResolver(validationSchema(t)),
	});

	const addScannerMutation = useMutation({
		mutationFn: (data: AddScannerFormData) => addMember({ ...data, role: 'scanner' }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['partnerSettings'] });
			showNotification({ message: t('Scanner added successfully'), type: TYPES.SUCCESS });
			onClose();
			formMethods.reset();
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error adding scanner', context: { error } });
			showNotification({ message: t('Error adding scanner'), type: TYPES.ERROR });
		},
	});

	const onSubmit = (data: AddScannerFormData) => {
		addScannerMutation.mutate(data);
	};

	if (!isOpen) return null;

	return (
		<Styled.StyledModal onClose={onClose}>
			<Styled.ModalContent>
				<Styled.HeaderContainer>
					<Styled.Title>{t('Add New Scanner')}</Styled.Title>
				</Styled.HeaderContainer>

				<Form onSubmit={onSubmit} formMethods={formMethods}>
					<Styled.FormContent>
						<Styled.InputContainer>
							<Styled.Label>{t('Full Name')}</Styled.Label>
							<Styled.Input {...formMethods.register('full_name')} placeholder={t('Enter scanner full name')} />
							{formMethods.formState.errors.full_name && (
								<Styled.ErrorMessage>{formMethods.formState.errors.full_name.message}</Styled.ErrorMessage>
							)}
						</Styled.InputContainer>

						<Styled.InputContainer>
							<Styled.Label>{t('Email')}</Styled.Label>
							<Styled.Input {...formMethods.register('email')} type="email" placeholder={t('Enter scanner email')} />
							{formMethods.formState.errors.email && (
								<Styled.ErrorMessage>{formMethods.formState.errors.email.message}</Styled.ErrorMessage>
							)}
						</Styled.InputContainer>
					</Styled.FormContent>

					<Styled.ButtonsContainer>
						<Styled.Button onClick={onClose} variant="secondary">
							{t('Cancel')}
						</Styled.Button>
						<Styled.Button type="submit" disabled={addScannerMutation.isLoading}>
							{addScannerMutation.isLoading ? (
								<>
									<Styled.LoadingIcon iconName="loading" size="M" />
									{t('Adding...')}
								</>
							) : (
								t('Add Scanner')
							)}
						</Styled.Button>
					</Styled.ButtonsContainer>
				</Form>
			</Styled.ModalContent>
		</Styled.StyledModal>
	);
};
